import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  ShieldExclamationIcon,
  LockOpenIcon
} from '@heroicons/react/24/outline';
import { adminAPI, User as APIUser } from '../../services/api';
import { usePaginatedTable } from '../../hooks/usePaginatedTable';
import { useUserDeletionImpact } from '../../hooks/useUserDeletionImpact';
import Pagination from '../Common/Pagination';
import SuspendUserModal from './SuspendUserModal';
import AddUserModal from './AddUserModal';
import BulkUploadModal from './BulkUploadModal';

interface User {
  id: string;
  numeric_id?: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  is_approved?: boolean;
  is_verified?: boolean;
  account_status?: 'active' | 'suspended' | 'banned';
  suspended_at?: string;
  suspension_expires_at?: string;
  suspension_reason?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeleteingUser] = useState<User | null>(null);
  const [suspendingUser, setSuspendingUser] = useState<User | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    is_approved: false,
    is_verified: false
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const filteredUsers = users.filter(user => {
    const normalizedSearch = searchTerm.toLowerCase().replace(/[\s\-()]/g, '');
    const normalizedPhone = user.phone.replace(/[\s\-()]/g, '');
    
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         normalizedPhone.includes(normalizedSearch);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    goToPage,
    setItemsPerPage,
    resetPagination
  } = usePaginatedTable({
    items: filteredUsers,
    initialItemsPerPage: 10
  });

  const {
    dependencies,
    loading: impactLoading,
    error: impactError,
    fetchDependencies,
    reset: resetImpact
  } = useUserDeletionImpact();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    resetPagination();
  }, [searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    if (deletingUser && deletingUser.numeric_id) {
      fetchDependencies(deletingUser.numeric_id);
    } else {
      resetImpact();
    }
  }, [deletingUser]);

  const fetchUsers = async () => {
    try {
      const apiUsers = await adminAPI.getAllUsers();
      const formattedUsers = apiUsers.map((user: APIUser) => ({
        ...user,
        name: `${user.first_name} ${user.last_name}`,
        status: user.approval_status || 'approved',
        created_at: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      is_approved: user.is_approved || false,
      is_verified: user.is_verified || false
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.phone.trim()) {
      setNotification({ type: 'error', message: 'Please fill in all required fields' });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    if (!/^\+?[\d\s\-()]+$/.test(editForm.phone)) {
      setNotification({ type: 'error', message: 'Please enter a valid phone number' });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    try {
      if (!editingUser.numeric_id) {
        setNotification({ type: 'error', message: 'Invalid user ID' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      const updatePayload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone,
        approval_status: editForm.is_approved ? 'approved' : 'pending',
        is_verified: editForm.is_verified
      };
      await adminAPI.updateUser(editingUser.numeric_id, updatePayload);
      await fetchUsers();
      setEditingUser(null);
      setNotification({ type: 'success', message: 'User updated successfully!' });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error updating user:', error);
      setNotification({ type: 'error', message: error.response?.data?.detail || 'Failed to update user' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteingUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;

    if (!deletingUser.numeric_id) {
      setNotification({ type: 'error', message: 'Invalid user ID' });
      setTimeout(() => setNotification(null), 5000);
      setDeleteingUser(null);
      return;
    }

    try {
      await adminAPI.deleteUser(deletingUser.numeric_id);
      await fetchUsers();
      setDeleteingUser(null);
      setNotification({ type: 'success', message: 'User deleted successfully!' });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setNotification({ type: 'error', message: error.response?.data?.detail || 'Failed to delete user' });
      setTimeout(() => setNotification(null), 5000);
      setDeleteingUser(null);
    }
  };

  const handleSuspendUser = async (data: {
    suspension_type: 'temporary' | 'permanent';
    reason: string;
    duration_hours?: number;
    notify_user: boolean;
  }) => {
    if (!suspendingUser?.numeric_id) return;

    try {
      await adminAPI.suspendUser(suspendingUser.numeric_id, data);
      await fetchUsers();
      setSuspendingUser(null);
      setNotification({ 
        type: 'success', 
        message: `User ${suspendingUser.name} has been suspended successfully!` 
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error suspending user:', error);
      setNotification({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to suspend user' 
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleUnsuspendUser = async (user: User) => {
    if (!user.numeric_id) return;

    const confirmed = window.confirm(
      `Are you sure you want to unsuspend ${user.name}? They will regain full access to their account.`
    );
    
    if (!confirmed) return;

    try {
      await adminAPI.unsuspendUser(user.numeric_id, {
        note: 'Account reinstated by admin',
        notify_user: true
      });
      await fetchUsers();
      setNotification({ 
        type: 'success', 
        message: `User ${user.name} has been unsuspended successfully!` 
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error unsuspending user:', error);
      setNotification({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to unsuspend user' 
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const getAccountStatusBadge = (accountStatus?: string) => {
    if (!accountStatus || accountStatus === 'active') {
      return null;
    }
    
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      suspended: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspended' },
      banned: { bg: 'bg-gray-900', text: 'text-white', label: 'Banned' }
    };
    
    const badge = badges[accountStatus];
    if (!badge) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-volexx ${badge.bg} ${badge.text}`}>
        <ShieldExclamationIcon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getRoleBadge = (role: string) => {
    const badges: Record<string, string> = {
      customer: 'bg-green-100 text-green-800',
      rider: 'bg-volexx-blue-100 text-volexx-blue-800',
      merchant: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800'
    };
    return badges[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'rejected':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-volexx-lg shadow-volexx border ${notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} px-6 py-4 max-w-md`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
              ) : (
                <XCircleIcon className="h-6 w-6 text-red-600 mr-3" />
              )}
              <p className={`font-semibold font-poppins ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-poppins">User Management</h1>
            <p className="mt-2 text-gray-600 font-poppins">Full control over all users in the system</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="px-6 py-3 bg-white border-2 border-sky-600 text-sky-600 rounded-lg hover:bg-sky-50 transition-colors font-semibold font-poppins flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Upload
            </button>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-semibold font-poppins flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              />
            </div>

            {/* Role Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="customer">Customers</option>
                <option value="rider">Dispatchers</option>
                <option value="merchant">Merchants</option>
                <option value="admin">Admins</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Total Users</p>
            <p className="text-2xl font-bold text-gray-900 font-poppins">{users.length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Customers</p>
            <p className="text-2xl font-bold text-green-600 font-poppins">{users.filter(u => u.role === 'customer').length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Dispatchers</p>
            <p className="text-2xl font-bold text-volexx-blue-600 font-poppins">{users.filter(u => u.role === 'rider').length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Admins</p>
            <p className="text-2xl font-bold text-red-600 font-poppins">{users.filter(u => u.role === 'admin').length}</p>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-volexx bg-volexx-blue-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-volexx-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900 font-poppins">{user.name}</div>
                          <div className="text-sm text-gray-500 font-poppins">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getRoleBadge(user.role)}`}>
                        {user.role === 'rider' ? 'Dispatcher' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getStatusBadge(user.status)}`}>
                          {getStatusIcon(user.status)}
                          <span className="ml-1">{user.status.charAt(0).toUpperCase() + user.status.slice(1)}</span>
                        </span>
                        {getAccountStatusBadge(user.account_status)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 font-poppins mb-1">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {user.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 font-poppins">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {user.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-poppins">
                      {user.created_at}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-3">
                          <button 
                            onClick={() => setSelectedUser(user)}
                            className="text-volexx-blue-600 hover:text-volexx-blue-900 font-semibold font-poppins transition-colors"
                          >
                            View
                          </button>
                          <button 
                            onClick={() => handleEditClick(user)}
                            className="text-green-600 hover:text-green-900 font-semibold font-poppins transition-colors inline-flex items-center"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteClick(user)}
                            className="text-red-600 hover:text-red-900 font-semibold font-poppins transition-colors inline-flex items-center"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        </div>
                        {user.account_status === 'suspended' ? (
                          <button 
                            onClick={() => handleUnsuspendUser(user)}
                            className="text-green-600 hover:text-green-900 font-semibold font-poppins transition-colors inline-flex items-center"
                          >
                            <LockOpenIcon className="h-4 w-4 mr-1" />
                            Unsuspend
                          </button>
                        ) : (
                          <button 
                            onClick={() => setSuspendingUser(user)}
                            className="text-orange-600 hover:text-orange-900 font-semibold font-poppins transition-colors inline-flex items-center"
                          >
                            <ShieldExclamationIcon className="h-4 w-4 mr-1" />
                            Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-poppins">No users found matching your criteria</p>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* View User Details Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-volexx-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 font-poppins">User Details</h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-poppins mb-4">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">Full Name</p>
                      <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">User ID</p>
                      <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">Role</p>
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getRoleBadge(selectedUser.role)}`}>
                        {selectedUser.role === 'rider' ? 'Dispatcher' : selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">Status</p>
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getStatusBadge(selectedUser.status)}`}>
                        {getStatusIcon(selectedUser.status)}
                        <span className="ml-1">{selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-poppins mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600 font-poppins">Email</p>
                        <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600 font-poppins">Phone</p>
                        <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-poppins mb-4">Account Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">Joined Date</p>
                      <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.created_at}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-poppins">Verified</p>
                      <p className="text-base font-semibold text-gray-900 font-poppins">{selectedUser.is_verified ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-volexx-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 font-poppins">Edit User</h2>
                <button
                  onClick={() => setEditingUser(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={editForm.is_approved}
                      onChange={(e) => setEditForm({ ...editForm, is_approved: e.target.checked })}
                      className="rounded border-gray-300 text-volexx-blue-600 focus:ring-volexx-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 font-poppins">Approved</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={editForm.is_verified}
                      onChange={(e) => setEditForm({ ...editForm, is_verified: e.target.checked })}
                      className="rounded border-gray-300 text-volexx-blue-600 focus:ring-volexx-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 font-poppins">Verified</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-volexx-blue-600 text-white rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-volexx-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-poppins text-center mb-2">Delete User</h2>
                
                {impactLoading && (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-volexx-blue-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-600 font-poppins">Analyzing dependencies...</p>
                  </div>
                )}

                {impactError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-volexx">
                    <p className="text-sm text-red-600 font-poppins">{impactError}</p>
                  </div>
                )}

                {!impactLoading && dependencies && (
                  <>
                    <p className="text-gray-600 font-poppins text-center mb-4">
                      Are you sure you want to delete <span className="font-bold">{deletingUser.name}</span>?
                    </p>

                    <div className="bg-gray-50 rounded-volexx p-4 mb-4 space-y-2">
                      <h3 className="text-sm font-semibold text-gray-900 font-poppins mb-3">Impact Preview:</h3>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 font-poppins">Orders:</span>
                        <span className="text-sm font-semibold text-gray-900 font-poppins">{dependencies.order_count}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 font-poppins">Vehicles:</span>
                        <span className="text-sm font-semibold text-gray-900 font-poppins">{dependencies.vehicle_count}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 font-poppins">Wallet Balance:</span>
                        <span className="text-sm font-semibold text-gray-900 font-poppins">
                          ${dependencies.wallet_balance.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {!dependencies.can_delete && dependencies.blocking_reasons.length > 0 && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-volexx">
                        <h4 className="text-sm font-semibold text-yellow-900 font-poppins mb-2">Cannot Delete User:</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {dependencies.blocking_reasons.map((reason, index) => (
                            <li key={index} className="text-sm text-yellow-800 font-poppins">{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dependencies.can_delete && (
                      <p className="text-sm text-gray-500 font-poppins text-center mb-4">
                        This action cannot be undone.
                      </p>
                    )}
                  </>
                )}

                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setDeleteingUser(null)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={impactLoading || (dependencies ? !dependencies.can_delete : false)}
                    className="px-6 py-2 bg-red-600 text-white rounded-volexx font-semibold font-poppins hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend User Modal */}
        {suspendingUser && (
          <SuspendUserModal
            user={suspendingUser}
            onClose={() => setSuspendingUser(null)}
            onConfirm={handleSuspendUser}
          />
        )}

        {/* Add User Modal */}
        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={() => {
            setShowAddUserModal(false);
            fetchUsers();
            setNotification({ type: 'success', message: 'User created successfully!' });
            setTimeout(() => setNotification(null), 5000);
          }}
        />

        {/* Bulk Upload Modal */}
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            fetchUsers();
            setNotification({ type: 'success', message: 'Users uploaded successfully!' });
            setTimeout(() => setNotification(null), 5000);
          }}
        />
      </div>
    </div>
  );
};

export default UserManagement;
